import React from "react";
import { Text, plainText } from "../../atoms/text/text";
import { Tooltip } from "../../atoms/tooltip/tooltip.client";
import type { LineupChannel } from "./channel-lineup.types";
import styles from "./locality-badge.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The Local / RSN / National badge and its ⓘ — between a channel's name and its number on a grid
 * row (GR4), and on a logo tile in a Genre Pack (GP7, ZP9).
 */
export const LOCALITY_KINDS = ["local", "rsn", "national"] as const;
export type LocalityKind = (typeof LOCALITY_KINDS)[number];

/**
 * Which badge a channel wears, or `null` for none. Exported so the row, the pack grid and the
 * search results reach one decision rather than three. `rsn-oom` deliberately returns `null` — GP17
 * lists out-of-market RSNs "with no badge".
 */
export function localityKindFor(
  channel: Pick<LineupChannel, "kind" | "isNational">
): LocalityKind | null {
  if (channel.isNational) {
    return "national";
  }
  if (channel.kind === "local") {
    return "local";
  }
  if (channel.kind === "rsn") {
    return "rsn";
  }
  return null;
}

export interface LocalityBadgeProps {
  kind: LocalityKind;
  /** The pill's wording. Absent or blank, no badge is drawn. */
  label?: string;
  /** The ⓘ text. Absent or blank, the pill has no ⓘ. */
  tooltip?: string;
  /** Layout only. */
  className?: string;
}

function LocalityBadgeImpl({ kind, label, tooltip, className }: LocalityBadgeProps) {
  // Own keys only: an inherited name like `toString` must not pass as a kind.
  const resolvedKind: LocalityKind = LOCALITY_KINDS.includes(kind) ? kind : "local";
  const text = plainText((label ?? "").trim());
  const content = (tooltip ?? "").trim();
  if (text === "") {
    return null;
  }

  return (
    <span className={[styles.badge, styles[resolvedKind], className].filter(Boolean).join(" ")}>
      <Text as="span" className={styles.text} variant="label">
        {text}
      </Text>
      {content !== "" && (
        /* Named by the badge it explains: a row can carry a Local badge and an RSN badge, and two
           triggers with one name tell a reader nothing. */
        <Tooltip content={content} label={text} />
      )}
    </span>
  );
}
export const LocalityBadge = withSafeRender(LocalityBadgeImpl, "LocalityBadge");

export default LocalityBadge;
